import React, { useEffect, useMemo, useState, useCallback, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import NodeCard from "./NodeCard";
import OccupancyLogPanel from "./OccupancyLogPanel";
import DeletionRequestPanel from "./DeletionRequestPanel";
import AssignMemberModal from "./AssignMemberModal";
import RemoveMemberModal from "./RemoveMemberModal";
import ReassignMemberModal from "./ReassignMemberModal";
import RequestDeleteModal from "./RequestDeleteModal";
import InitTreeModal from "./InitTreeModal";
import TreeHealthPanel from "./TreeHealthPanel";
import PaginatedTreeView from "./PaginatedTreeView";
import MemberDetailPanel from "./MemberDetailPanel";
import DrilldownView from "./DrilldownView";
import QuickActionsMenu from "./QuickActionsMenu";
import MyTeamView from "./MyTeamView";
import LogFollowUpModal from "./LogFollowUpModal";
import ActivityFeed from "./ActivityFeed";
import AlphaActivityDashboard from "./AlphaActivityDashboard";
import AdminTreeView from "./AdminTreeView";
import { CustomIcon } from "../shared/CustomIcon";
import {
  apiTreeGet, apiTreeInit, apiTreeCreateNode, apiTreeAssign, apiTreeRemove,
  apiTreeReassign, apiTreeLookupSlot, apiTreeLookupMember, apiTreeOrphaned,
  apiDelReqCreate, apiDelReqList, apiTreeHealthMap,
} from "./api";

const FullTreeCanvas = lazy(() => import("./FullTreeCanvas"));

// =============================================================
// FollowUpTreePage (Phase 2) — orchestrates all admin flows via
// proper Kingdom Connect modals. No prompt/alert/confirm.
// =============================================================
export default function FollowUpTreePage({ currentUser, members, isSuperAdmin, isLimitedAdmin }) {
  const navigate = useNavigate();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [orphaned, setOrphaned] = useState([]);
  const [delReqs, setDelReqs] = useState([]);

  // View mode: 'myteam' (default) | 'paginated' | 'admin' | 'drilldown' (mobile only)
  const [viewMode, setViewMode] = useState("myteam");
  const [focusNodeId, setFocusNodeId] = useState(null);
  const [detailNode, setDetailNode] = useState(null);
  const [fullTreeOpen, setFullTreeOpen] = useState(false);
  const [quickActionNode, setQuickActionNode] = useState(null);
  const [logModalNode, setLogModalNode] = useState(null);
  const [activityRefresh, setActivityRefresh] = useState(0);
  const [healthMap, setHealthMap] = useState({});

  // Load the server-computed health map (drives dot colors everywhere).
  const loadHealthMap = useCallback(async () => {
    try {
      const r = await apiTreeHealthMap();
      setHealthMap(r.map || {});
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadHealthMap(); }, [loadHealthMap, activityRefresh]);

  // Mobile detection (<768px). Controls whether the drilldown toggle appears.
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Modals
  const [logNode, setLogNode] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [removeFor, setRemoveFor] = useState(null);
  const [reassignFor, setReassignFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [initOpen, setInitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Admin drawer
  const [drawer, setDrawer] = useState("search"); // 'search' | 'health' | 'reassign' | 'deletion'

  // Search
  const [searchSlot, setSearchSlot] = useState("");
  const [searchNum, setSearchNum] = useState("");
  const [searchResult, setSearchResult] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);
  const flashToast = useCallback((text, tone = "success") => {
    setToast({ text, tone, id: Date.now() });
  }, []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const t = await apiTreeGet();
      setTree(t);
      if (isSuperAdmin) {
        try { const o = await apiTreeOrphaned(); setOrphaned(Array.isArray(o) ? o : []); } catch { /* silent */ }
        try { const r = await apiDelReqList(); setDelReqs(Array.isArray(r) ? r : []); } catch { /* silent */ }
      }
    } catch (e) {
      setError(e?.message || "Failed to load tree");
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);
  useEffect(() => { load(); }, [load]);

  const nodesArr = useMemo(() => tree ? Object.values(tree.nodes || {}) : [], [tree]);
  const nodesByLevel = useMemo(() => {
    const out = {};
    nodesArr.forEach(n => {
      if (n.isGodNode) return; // God is not a human level
      (out[n.level] = out[n.level] || []).push(n);
    });
    Object.keys(out).forEach(k => out[k].sort((a, b) => (a.slotRef || "").localeCompare(b.slotRef || "")));
    return out;
  }, [nodesArr]);
  // Members already occupying an active node (excluded from Assign search)
  const activeAssignments = useMemo(() => {
    const map = {};
    nodesArr.forEach(n => { if (n.memberId && n.status === "Occupied") map[n.memberId] = n.slotRef; });
    return map;
  }, [nodesArr]);

  // Resolve the "My Team" landing node for the current user: their own node
  // if present in the tree, else the first occupied Alpha (so God appears as
  // the leader row for admins without a member profile).
  const myTeamFocusId = useMemo(() => {
    if (!tree) return null;
    if (currentUser?.memberId) {
      const mine = Object.values(tree.nodes || {}).find(n => n.memberId === currentUser.memberId && n.status === "Occupied");
      if (mine) return mine.nodeId;
    }
    // Fallback for admins-without-node: first assigned Alpha (God-as-leader).
    const firstOccupiedAlpha = (tree.alphaNodeIds || [])
      .map(id => tree.nodes?.[id])
      .find(n => n && n.memberId && n.status === "Occupied");
    if (firstOccupiedAlpha) return firstOccupiedAlpha.nodeId;
    return (tree.alphaNodeIds || [])[0] || tree.godNodeId || tree.alphaNodeId || null;
  }, [tree, currentUser?.memberId]);

  // Auto-focus the user's node on first tree load (My Team default).
  useEffect(() => {
    if (myTeamFocusId && !focusNodeId) setFocusNodeId(myTeamFocusId);
  }, [myTeamFocusId, focusNodeId]);

  const rootAnchorId = tree?.godNodeId || tree?.alphaNodeId || null;

  if (!isSuperAdmin && !isLimitedAdmin) {
    // Non-admins still see the tree; only admin tools are gated.
    const userNode = currentUser?.memberId
      ? Object.values(tree?.nodes || {}).find(n => n.memberId === currentUser.memberId && n.status === "Occupied")
      : null;
    if (tree && (tree.godNodeId || tree.alphaNodeId) && !userNode) {
      // fall through — still render the tree, they'll see God + Alpha slots
    }
  }

  // ----- Handlers (open modals + call APIs) -----
  const handleInit = async (memberId) => {
    setSubmitting(true);
    try {
      await apiTreeInit(memberId);
      setInitOpen(false);
      await load();
      const m = memberId ? (members || []).find(x => x.id === memberId) : null;
      const name = m ? `${m.firstName || ""} ${m.lastName || ""}`.trim() : "Vacant Alpha";
      flashToast(m ? `${name} (#${m.memberNumber || "—"}) assigned to AL-001` : "Alpha node created (vacant)");
    } catch (e) { flashToast(e?.message || "Init failed", "error"); }
    finally { setSubmitting(false); }
  };

  const handleAssign = async (member) => {
    if (!assignFor) return;
    setSubmitting(true);
    try {
      await apiTreeAssign(assignFor.nodeId, member.id);
      const slotRef = assignFor.slotRef;
      setAssignFor(null);
      await load();
      const name = `${member.firstName || ""} ${member.lastName || ""}`.trim();
      flashToast(`${name} (#${member.memberNumber || "—"}) assigned to ${slotRef}`);
    } catch (e) { flashToast(e?.message || "Assign failed", "error"); }
    finally { setSubmitting(false); }
  };

  const handleRemove = async (reason, notes) => {
    if (!removeFor) return;
    setSubmitting(true);
    try {
      await apiTreeRemove(removeFor.nodeId, reason, notes);
      const slotRef = removeFor.slotRef;
      setRemoveFor(null);
      await load();
      flashToast(`Removed from ${slotRef}. Direct reports notified.`);
    } catch (e) { flashToast(e?.message || "Remove failed", "error"); }
    finally { setSubmitting(false); }
  };

  const handleReassign = async (destNode) => {
    if (!reassignFor) return;
    setSubmitting(true);
    try {
      await apiTreeReassign(reassignFor.nodeId, destNode.nodeId);
      const fromRef = reassignFor.slotRef, toRef = destNode.slotRef;
      setReassignFor(null);
      await load();
      flashToast(`Reassigned from ${fromRef} → ${toRef}`);
    } catch (e) { flashToast(e?.message || "Reassign failed", "error"); }
    finally { setSubmitting(false); }
  };

  const handleAddChild = async (node) => {
    setSubmitting(true);
    try {
      const created = await apiTreeCreateNode(node.nodeId);
      await load();
      flashToast(`Created ${created.slotRef} under ${node.slotRef}`);
    } catch (e) { flashToast(e?.message || "Create child failed", "error"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteRequest = async (reason) => {
    if (!deleteFor) return;
    setSubmitting(true);
    try {
      await apiDelReqCreate("node", deleteFor.nodeId, reason);
      const slotRef = deleteFor.slotRef;
      setDeleteFor(null);
      await load();
      flashToast(`Deletion request submitted for ${slotRef}. Other admins notified.`);
    } catch (e) { flashToast(e?.message || "Request failed", "error"); }
    finally { setSubmitting(false); }
  };

  const doSearch = async (type) => {
    setSearchResult(null);
    try {
      if (type === "slot" && searchSlot.trim()) {
        const r = await apiTreeLookupSlot(searchSlot.trim());
        setSearchResult({ kind: "slot", data: r });
      } else if (type === "member" && searchNum.trim()) {
        const r = await apiTreeLookupMember(searchNum.trim());
        setSearchResult({ kind: "member", data: r });
      }
    } catch (e) {
      setSearchResult({ kind: "error", data: { detail: e?.message || "Not found" } });
    }
  };

  // ---- Render helpers ----
  const renderSearch = () => (
    <div data-testid="tree-search-panel" style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 12, padding: 16 }}>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Search</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={{ fontSize: 11, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>By slot reference</label>
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <input data-testid="search-slot-input" value={searchSlot} onChange={e => setSearchSlot(e.target.value)} placeholder="e.g. BE-002" className="form-input" style={{ flex: 1 }} />
            <button className="btn btn-sm btn-outline" data-testid="search-slot-btn" onClick={() => doSearch("slot")}>Search</button>
          </div>
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 4, lineHeight: 1.4 }}>
            Enter a two-letter prefix and slot number to see everyone who held that slot.
          </div>
          {!searchResult && (
            <div data-testid="search-slot-mock" style={{ fontSize: 11, color: "var(--kc-meta)", marginTop: 6, padding: "6px 8px", border: "1px dashed color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 6, fontStyle: "italic" }}>
              Example result: Slot BE-002 · 3 occupancies · #0000012 Sarah Doe (2025-01) → (2025-11 · Relocated)
            </div>
          )}
        </div>
        <div>
          <label style={{ fontSize: 11, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>By member number</label>
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <input data-testid="search-num-input" value={searchNum} onChange={e => setSearchNum(e.target.value)} placeholder="e.g. 0000047" className="form-input" style={{ flex: 1 }} />
            <button className="btn btn-sm btn-outline" data-testid="search-num-btn" onClick={() => doSearch("member")}>Search</button>
          </div>
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 4, lineHeight: 1.4 }}>
            Enter a 7-digit member number to see all slots this member has occupied.
          </div>
          {!searchResult && (
            <div data-testid="search-num-mock" style={{ fontSize: 11, color: "var(--kc-meta)", marginTop: 6, padding: "6px 8px", border: "1px dashed color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 6, fontStyle: "italic" }}>
              Example result: #0000047 held BE-002 (2024-06 → 2025-11) and GA-005 (2025-11 → present)
            </div>
          )}
        </div>
      </div>
      {searchResult && (
        <div data-testid="search-result" style={{ marginTop: 12, padding: 12, background: "color-mix(in srgb, var(--kc-accent) 5%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 8, fontSize: 12, color: "var(--kc-text)" }}>
          {searchResult.kind === "error" && <div style={{ color: "#ff6b6b" }}>{searchResult.data.detail}</div>}
          {searchResult.kind === "slot" && (
            <>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Slot {searchResult.data.slotRef} · {searchResult.data.occupancyLog.length} occupancies</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {searchResult.data.occupancyLog.map((e, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: 6, background: "rgba(255,255,255,0.03)", borderRadius: 8 }}>
                    {e.memberPhoto ? (
                      <img src={e.memberPhoto} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#333", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "var(--kc-accent)" }}>#</div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: "var(--kc-text)" }}>#{e.memberNumber} — {e.memberName}</div>
                      <div style={{ fontSize: 10, color: "var(--kc-sub)" }}>
                        {(e.dateAssigned || "").slice(0, 10)} → {(e.dateDeparted || "active").slice(0, 10)}{e.departureReason ? ` · ${e.departureReason}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {searchResult.kind === "member" && (
            <>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>#{searchResult.data.memberNumber} · {searchResult.data.positions.length} position(s)</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {searchResult.data.positions.map((p, i) => (
                  <div key={i} style={{ padding: 6, background: "rgba(255,255,255,0.03)", borderRadius: 8, fontSize: 12 }}>
                    <div><strong style={{ color: "var(--kc-accent)", fontFamily: "'Cinzel',serif" }}>{p.slotRef}</strong> ({p.levelName})</div>
                    <div style={{ fontSize: 10, color: "var(--kc-sub)", marginTop: 2 }}>
                      {(p.dateAssigned || "").slice(0, 10)} → {(p.dateDeparted || "active").slice(0, 10)}{p.departureReason ? ` · ${p.departureReason}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="followuptree-page" style={{ padding: "20px clamp(12px,3vw,28px)" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Follow-Up Tree
          </div>
          <div style={{ fontSize: 12, color: "var(--kc-sub)", marginTop: 3 }}>
            Ternary discipleship tree · 24 levels (Alpha → Omega)
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {currentUser?.memberId && Object.values(tree?.nodes || {}).some(n => n.memberId === currentUser.memberId && n.status === "Occupied") && (
            <button
              className="btn btn-sm btn-gold"
              data-testid="tree-my-tree"
              onClick={() => {
                const mine = Object.values(tree.nodes).find(n => n.memberId === currentUser.memberId && n.status === "Occupied");
                if (mine) {
                  setFocusNodeId(mine.nodeId);
                  setViewMode("drilldown");
                }
              }}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            ><CustomIcon name="celebrate" size={12} /> My Tree</button>
          )}
          <button
            className={`btn btn-sm ${viewMode === "drilldown" ? "btn-gold" : "btn-outline"}`}
            data-testid="tree-drilldown-toggle"
            onClick={() => setViewMode(viewMode === "drilldown" ? "myteam" : "drilldown")}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <CustomIcon name={viewMode === "drilldown" ? "people" : "arrow-right"} size={12} />
            {viewMode === "drilldown" ? "My Team" : "Drill Down"}
          </button>
          {(isSuperAdmin || isLimitedAdmin || (myTeamFocusId && (tree?.nodes?.[myTeamFocusId]?.level === 1))) && (
            <button
              className={`btn btn-sm ${viewMode === "dashboard" ? "btn-gold" : "btn-outline"}`}
              data-testid="tree-dashboard-toggle"
              onClick={() => setViewMode(viewMode === "dashboard" ? "myteam" : "dashboard")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            ><CustomIcon name="chart" size={12} /> Dashboard</button>
          )}
          {isSuperAdmin && tree && rootAnchorId && (
            <button
              className="btn btn-sm btn-outline"
              data-testid="tree-full-toggle"
              onClick={() => setFullTreeOpen(true)}
            >Full Tree</button>
          )}
          {(isSuperAdmin || isLimitedAdmin) && (
            <button
              className="btn btn-sm btn-outline"
              data-testid="tree-view-toggle"
              onClick={() => setViewMode(viewMode === "admin" ? "myteam" : "admin")}
            >{viewMode === "admin" ? "My Team View" : "Admin View"}</button>
          )}
          <button className="btn btn-sm btn-outline" data-testid="tree-legacy-archive" onClick={() => navigate("/follow-ups-archive")}>Log Entry</button>
          {(isSuperAdmin || isLimitedAdmin) && (
            <>
              <button
                className="btn btn-sm btn-outline"
                data-testid="tree-export-csv"
                onClick={() => {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/followuptree/export/csv`;
                  const token = localStorage.getItem("kc_token");
                  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.blob())
                    .then(blob => {
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `Kingdom-Connect-FollowUp-Tree-${new Date().toISOString().slice(0,10)}.csv`;
                      a.click();
                      setTimeout(() => URL.revokeObjectURL(a.href), 500);
                    })
                    .catch(() => flashToast("Export failed", "error"));
                }}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              ><CustomIcon name="download-inbox" size={12} /> CSV</button>
              <button
                className="btn btn-sm btn-outline"
                data-testid="tree-export-pdf"
                onClick={() => {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/followuptree/export/pdf`;
                  const token = localStorage.getItem("kc_token");
                  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.text())
                    .then(html => {
                      const w = window.open("", "_blank");
                      if (w) { w.document.write(html); w.document.close(); }
                    })
                    .catch(() => flashToast("Export failed", "error"));
                }}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              ><CustomIcon name="download-inbox" size={12} /> PDF</button>
            </>
          )}
          <button className="btn btn-sm btn-outline" data-testid="tree-refresh" onClick={load}>Refresh</button>
        </div>
      </div>

      {loading && <div style={{ color: "var(--kc-sub)" }}>Loading tree…</div>}
      {error && <div data-testid="tree-error" style={{ color: "#ff6b6b" }}>{error}</div>}

      {tree && !rootAnchorId && (
        <div data-testid="tree-empty" style={{ background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 12, padding: 24, textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 16, color: "var(--kc-accent)", marginBottom: 8 }}>
            Tree loading…
          </div>
        </div>
      )}

      {/* View router: My Team (default) → 4-row relational view.
          Drilldown → infinite mobile-friendly navigation.
          Paginated → 1-3-9 legacy view (still available).
          Admin → level-grouped grid (admins only). */}
      {tree && rootAnchorId && viewMode === "myteam" && (
        <>
          <MyTeamView
            tree={tree}
            focusNodeId={focusNodeId || myTeamFocusId}
            members={members}
            currentUser={currentUser}
            healthMap={healthMap}
            onFocusNode={(id) => setFocusNodeId(id)}
            onOpenNode={(n) => setDetailNode(n)}
            onDrillDown={(n) => { setFocusNodeId(n.nodeId); setViewMode("drilldown"); }}
          />
          <ActivityFeed refreshToken={activityRefresh} onOpenMember={(mid) => navigate(`/members/${mid}`)} />
        </>
      )}
      {tree && rootAnchorId && viewMode === "drilldown" && (
        <DrilldownView
          tree={tree}
          members={members}
          currentUser={currentUser}
          isSuperAdmin={isSuperAdmin}
          isLimitedAdmin={isLimitedAdmin}
          initialFocus={focusNodeId}
          healthMap={healthMap}
          onOpenNode={(n) => setDetailNode(n)}
          onOpenActions={(n) => setQuickActionNode(n)}
        />
      )}
      {tree && rootAnchorId && viewMode === "dashboard" && (
        <AlphaActivityDashboard
          currentUser={currentUser}
          onOpenMember={(mid) => navigate(`/members/${mid}`)}
        />
      )}
      {tree && rootAnchorId && viewMode === "paginated" && (
        <PaginatedTreeView
          tree={tree}
          focusNodeId={focusNodeId || rootAnchorId}
          members={members}
          currentUser={currentUser}
          highlightUserNode={true}
          healthMap={healthMap}
          onFocusNode={(id) => setFocusNodeId(id)}
          onOpenNode={(n) => setDetailNode(n)}
          onBackToTop={() => setFocusNodeId(rootAnchorId)}
        />
      )}
      {tree && rootAnchorId && viewMode === "admin" && (
        <AdminTreeView
          nodesArr={nodesArr}
          nodesByLevel={nodesByLevel}
          members={members}
          currentUser={currentUser}
          isSuperAdmin={isSuperAdmin}
          isLimitedAdmin={isLimitedAdmin}
          orphaned={orphaned}
          delReqs={delReqs}
          onAssign={setAssignFor}
          onRemove={setRemoveFor}
          onReassign={setReassignFor}
          onAddChild={handleAddChild}
          onViewLog={setLogNode}
          onRequestDelete={setDeleteFor}
          onSwitchToMyTeam={() => setViewMode("myteam")}
          onOpenFullTree={() => setFullTreeOpen(true)}
          onSwitchToDrilldown={() => setViewMode("drilldown")}
          onOpenSearchPanel={() => setDrawer("search")}
          onOpenHealthPanel={() => setDrawer("health")}
          onOpenReassignPanel={() => setDrawer("reassign")}
          onOpenDeletionPanel={() => setDrawer("deletion")}
        />
      )}

      {/* Admin tools drawer (tabs) — SA only */}
      {isSuperAdmin && tree && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", gap: 6, borderBottom: "2px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", marginBottom: 14, flexWrap: "wrap" }}>
            {[
              { id: "search", label: "Search" },
              { id: "health", label: "Tree Health" },
              { id: "reassign", label: `Needs Reassignment (${orphaned.length})`, testid: "tab-reassign" },
              { id: "deletion", label: "Deletion Requests" },
            ].map(t => (
              <button
                key={t.id}
                data-testid={t.testid || `tab-${t.id}`}
                onClick={() => setDrawer(t.id)}
                style={{
                  padding: "10px 16px", fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
                  border: "none", background: "none", cursor: "pointer",
                  color: drawer === t.id ? "var(--kc-text)" : "var(--kc-sub)",
                  borderBottom: `2px solid ${drawer === t.id ? "var(--kc-accent)" : "transparent"}`,
                  marginBottom: "-2px", fontFamily: "'Montserrat',sans-serif",
                }}
              >{t.label}</button>
            ))}
          </div>

          {drawer === "search" && renderSearch()}

          {drawer === "health" && (
            <TreeHealthPanel
              tree={tree}
              orphaned={orphaned}
              deletionRequests={delReqs}
              onOpenAssignForOrphan={(o) => {
                const dstNode = nodesArr.find(n => n.nodeId === o.nodeId);
                if (dstNode) setReassignFor(dstNode);
              }}
            />
          )}

          {drawer === "reassign" && (
            <div data-testid="reassign-panel" style={{ background: "var(--kc-surface)", border: "1px solid rgba(255,107,107,0.20)", borderRadius: 12, padding: 16 }}>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, color: "#ff6b6b", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>
                Needs Reassignment ({orphaned.length})
              </div>
              {orphaned.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--kc-sub)" }}>No orphaned reports right now.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {orphaned.map(o => {
                    const m = (members || []).find(x => x.id === o.memberId) || {};
                    return (
                      <div key={o.nodeId} data-testid={`orphan-row-${o.nodeId}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, background: "rgba(255,107,107,0.06)", border: "1px solid rgba(255,107,107,0.20)", borderRadius: 10 }}>
                        {m.photo ? (
                          <img src={m.photo} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover" }} />
                        ) : (
                          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--kc-accent)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12 }}>
                            {(m.firstName?.[0] || "?") + (m.lastName?.[0] || "")}
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: "var(--kc-text)" }}>
                            {(m.firstName || "") + " " + (m.lastName || "")} <span style={{ color: "var(--kc-accent)", fontFamily: "'Cinzel',serif" }}>#{o.memberNumber}</span>
                          </div>
                          <div style={{ fontSize: 11, color: "var(--kc-sub)" }}>
                            Current slot {o.slotRef} · parent {o.parentSlotRef} ({o.parentStatus})
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-gold"
                          data-testid={`orphan-reassign-${o.nodeId}`}
                          onClick={() => {
                            const src = nodesArr.find(n => n.nodeId === o.nodeId);
                            if (src) setReassignFor(src);
                          }}
                          style={{ whiteSpace: "nowrap" }}
                        >Assign to New Slot</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {drawer === "deletion" && (
            <DeletionRequestPanel currentUser={currentUser} onChange={load} />
          )}
        </div>
      )}

      {/* ----- Modals ----- */}
      {initOpen && (
        <InitTreeModal members={members} onConfirm={handleInit} onClose={() => setInitOpen(false)} submitting={submitting} />
      )}
      {assignFor && (
        <AssignMemberModal
          node={assignFor}
          members={members}
          activeAssignments={activeAssignments}
          onConfirm={handleAssign}
          onClose={() => setAssignFor(null)}
          submitting={submitting}
        />
      )}
      {removeFor && (
        <RemoveMemberModal
          node={removeFor}
          member={(members || []).find(m => m.id === removeFor.memberId)}
          orphanCount={(removeFor.childNodeIds || []).filter(cid => (tree?.nodes?.[cid] || {}).memberId).length}
          onConfirm={handleRemove}
          onClose={() => setRemoveFor(null)}
          submitting={submitting}
        />
      )}
      {reassignFor && (
        <ReassignMemberModal
          node={reassignFor}
          member={(members || []).find(m => m.id === reassignFor.memberId)}
          allNodes={nodesArr}
          onConfirm={handleReassign}
          onClose={() => setReassignFor(null)}
          submitting={submitting}
        />
      )}
      {deleteFor && (
        <RequestDeleteModal
          node={deleteFor}
          onConfirm={handleDeleteRequest}
          onClose={() => setDeleteFor(null)}
          submitting={submitting}
        />
      )}
      {logNode && <OccupancyLogPanel node={logNode} onClose={() => setLogNode(null)} />}

      {/* Quick actions bottom sheet (drilldown) */}
      {quickActionNode && (
        <QuickActionsMenu
          node={quickActionNode}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setQuickActionNode(null)}
          onLogFollowUp={(n) => setLogModalNode(n)}
          onViewProfile={(n) => { if (n?.memberId) navigate(`/members/${n.memberId}`); }}
          onReassign={(n) => setReassignFor(n)}
          onRemove={(n) => setRemoveFor(n)}
        />
      )}

      {/* Log Follow-Up modal */}
      {logModalNode && (
        <LogFollowUpModal
          node={logModalNode}
          member={logModalNode.memberId ? (members || []).find(m => m.id === logModalNode.memberId) : null}
          onClose={() => setLogModalNode(null)}
          onLogged={() => { setActivityRefresh(t => t + 1); load(); flashToast("Follow-up logged."); }}
        />
      )}

      {/* Member detail panel (Phase 3) */}
      {detailNode && (
        <MemberDetailPanel
          node={detailNode}
          tree={tree}
          members={members}
          currentUser={currentUser}
          isSuperAdmin={isSuperAdmin}
          isLimitedAdmin={isLimitedAdmin}
          onClose={() => setDetailNode(null)}
          onNavigateNode={(nid) => { setDetailNode(tree?.nodes?.[nid] || null); setFocusNodeId(nid); }}
          onOpenAssign={(n) => { setDetailNode(null); setAssignFor(n); }}
          onOpenReassign={(n) => { setDetailNode(null); setReassignFor(n); }}
          onOpenRemove={(n) => { setDetailNode(null); setRemoveFor(n); }}
          onOpenViewProfile={(m) => navigate(`/members/${m.id}`)}
        />
      )}

      {/* Full Tree canvas (Super Admin only) */}
      {fullTreeOpen && isSuperAdmin && tree && (
        <Suspense fallback={
          <div data-testid="ftc-loading" style={{ position: "fixed", inset: 0, background: "#0e0e0e", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--kc-accent)", fontFamily: "'Montserrat',sans-serif" }}>
            Loading full tree canvas…
          </div>
        }>
          <FullTreeCanvas
            tree={tree}
            members={members}
            currentUser={currentUser}
            isSuperAdmin={isSuperAdmin}
            healthMap={healthMap}
            onOpenNode={(n) => { setFullTreeOpen(false); setDetailNode(n); }}
            onClose={() => setFullTreeOpen(false)}
            onReloadTree={load}
            onToast={(msg, kind) => flashToast(msg, kind || "success")}
          />
        </Suspense>
      )}

      {/* Toast */}
      {toast && (
        <div
          data-testid={`toast-${toast.tone}`}
          role="status"
          style={{
            position: "fixed", left: 16, right: 16, bottom: "calc(80px + env(safe-area-inset-bottom))",
            background: toast.tone === "error" ? "rgba(255,107,107,0.95)" : "rgba(74,222,128,0.95)",
            color: "#0A0A0A", borderRadius: 12, padding: "12px 16px",
            fontSize: 13, fontWeight: 700, fontFamily: "'Montserrat',sans-serif",
            zIndex: 12000, boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
            display: "flex", alignItems: "center", gap: 8, maxWidth: 640, margin: "0 auto",
          }}
        >
          <CustomIcon name={toast.tone === "error" ? "warning" : "check"} size={14} />
          <span>{toast.text}</span>
        </div>
      )}
    </div>
  );
}
